import { ChevronRight } from 'lucide-react';
import * as React from 'react';
import { createPortal } from 'react-dom';
import toast from 'react-hot-toast';
import {
  canClearTableMenuTarget,
  executeSuperDocTableMenuAction,
  resolveSuperDocTableMenuTarget,
  SUPERDOC_TABLE_COMMANDS,
  tableFragmentFromPoint,
  type SuperDocTableMenuAction,
  type SuperDocTableMenuPoint,
  type SuperDocTableMenuTarget,
} from '@/lib/superdocTableContextMenu';
import { cn } from '@/lib/utils';

type OpenSubmenu = 'row' | 'column' | null;

interface MenuPosition {
  x: number;
  y: number;
}

const MENU_PAD_PX = 8;
const ACTION_FAILURE_MESSAGE = 'Could not update the table';

const MENU_ACTIONS = {
  'insert-row-above': { type: 'command', id: SUPERDOC_TABLE_COMMANDS.insertRowAbove },
  'insert-row-below': { type: 'command', id: SUPERDOC_TABLE_COMMANDS.insertRowBelow },
  'insert-column-left': { type: 'command', id: SUPERDOC_TABLE_COMMANDS.insertColumnLeft },
  'insert-column-right': { type: 'command', id: SUPERDOC_TABLE_COMMANDS.insertColumnRight },
  'delete-row': { type: 'command', id: SUPERDOC_TABLE_COMMANDS.deleteRow },
  'delete-column': { type: 'command', id: SUPERDOC_TABLE_COMMANDS.deleteColumn },
  'clear-cell-content': { type: 'clear-cell-content' },
  'delete-table': { type: 'command', id: SUPERDOC_TABLE_COMMANDS.deleteTable },
} as const satisfies Record<string, SuperDocTableMenuAction>;

type MenuActionKey = keyof typeof MENU_ACTIONS;

function clampMenuPosition(x: number, y: number, width: number, height: number): MenuPosition {
  return {
    x: Math.max(MENU_PAD_PX, Math.min(x, window.innerWidth - width - MENU_PAD_PX)),
    y: Math.max(MENU_PAD_PX, Math.min(y, window.innerHeight - height - MENU_PAD_PX)),
  };
}

function preventNativeMenu(event: React.MouseEvent) {
  event.preventDefault();
}

function MenuItem({
  action,
  children,
  className,
  destructive = false,
  disabled = false,
  onClick,
  onMouseEnter,
}: {
  action: MenuActionKey;
  children: React.ReactNode;
  className?: string;
  destructive?: boolean;
  disabled?: boolean;
  onClick: React.MouseEventHandler<HTMLButtonElement>;
  onMouseEnter?: React.MouseEventHandler<HTMLButtonElement>;
}) {
  return (
    <button
      type="button"
      className={cn(
        'flex w-full cursor-default select-none items-center rounded-xs px-2 py-1.5 text-left text-sm outline-hidden',
        disabled
          ? 'pointer-events-none opacity-50'
          : destructive
            ? 'text-red-600 hover:bg-red-50'
            : 'text-stone-950 hover:bg-stone-100',
        className,
      )}
      data-action={action}
      disabled={disabled}
      onClick={disabled ? undefined : onClick}
      onMouseEnter={onMouseEnter}
    >
      {children}
    </button>
  );
}

function SubmenuItem({
  label,
  open,
  onOpen,
  children,
}: {
  label: string;
  open: boolean;
  onOpen: () => void;
  children: React.ReactNode;
}) {
  return (
    <div className="relative" onMouseEnter={onOpen} onFocus={onOpen}>
      <button
        type="button"
        className={cn(
          'flex w-full cursor-default select-none items-center rounded-xs px-2 py-1.5 text-left text-sm outline-hidden',
          'text-stone-950 hover:bg-stone-100',
          open && 'bg-stone-100',
        )}
        onClick={onOpen}
      >
        {label}
        <ChevronRight className="ml-auto size-4 shrink-0" />
      </button>
      {open ? (
        <div className="absolute top-0 left-full z-10 pl-1">
          <div className="min-w-36 overflow-hidden rounded-md border border-stone-200 bg-white p-1 shadow-md">
            {children}
          </div>
        </div>
      ) : null}
    </div>
  );
}

export function DraftDocxTableContextMenu({
  enabled,
  getSource,
  getStage,
  onMutated,
}: {
  enabled: boolean;
  getSource: () => unknown;
  getStage: () => ParentNode | null | undefined;
  onMutated?: () => void;
}) {
  const menuRef = React.useRef<HTMLDivElement>(null);
  const getSourceRef = React.useRef(getSource);
  const getStageRef = React.useRef(getStage);
  const onMutatedRef = React.useRef(onMutated);
  const pointRef = React.useRef<SuperDocTableMenuPoint | null>(null);
  const [menu, setMenu] = React.useState<MenuPosition | null>(null);
  const [pos, setPos] = React.useState<MenuPosition>({ x: 0, y: 0 });
  const [openSubmenu, setOpenSubmenu] = React.useState<OpenSubmenu>(null);
  const [menuTarget, setMenuTarget] = React.useState<SuperDocTableMenuTarget | null>(null);

  getSourceRef.current = getSource;
  getStageRef.current = getStage;
  onMutatedRef.current = onMutated;

  const closeMenu = React.useCallback(() => {
    pointRef.current = null;
    setMenu(null);
    setMenuTarget(null);
    setOpenSubmenu(null);
  }, []);

  const openRowSubmenu = React.useCallback(() => setOpenSubmenu('row'), []);
  const openColumnSubmenu = React.useCallback(() => setOpenSubmenu('column'), []);
  const closeSubmenu = React.useCallback(() => setOpenSubmenu(null), []);
  const canDeleteCell = canClearTableMenuTarget(menuTarget);

  React.useLayoutEffect(() => {
    if (!menu) return;
    const el = menuRef.current;
    if (!el) {
      setPos(menu);
      return;
    }
    const { width, height } = el.getBoundingClientRect();
    setPos(clampMenuPosition(menu.x, menu.y, width, height));
  }, [menu]);

  React.useEffect(() => {
    if (!enabled) {
      closeMenu();
      return undefined;
    }

    const handleContextMenu = (event: MouseEvent) => {
      const stage = getStageRef.current();
      const target = event.target;
      if (!(stage instanceof Node) || !(target instanceof Node) || !stage.contains(target)) return;
      if (!(stage instanceof Element) || !stage.classList.contains('draft-docx-editor--editing')) return;

      const fragment = tableFragmentFromPoint(event.clientX, event.clientY, event.target);
      if (!fragment || !stage.contains(fragment)) return;

      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();

      const point: SuperDocTableMenuPoint = {
        clientX: event.clientX,
        clientY: event.clientY,
        fragment,
        stage,
      };
      pointRef.current = point;
      setMenuTarget(null);
      setOpenSubmenu(null);
      setMenu({ x: event.clientX, y: event.clientY });

      void resolveSuperDocTableMenuTarget(getSourceRef.current(), point).then((resolved) => {
        if (pointRef.current !== point) return;
        setMenuTarget(resolved);
      });
    };

    document.addEventListener('contextmenu', handleContextMenu, true);
    return () => {
      document.removeEventListener('contextmenu', handleContextMenu, true);
    };
  }, [closeMenu, enabled]);

  React.useEffect(() => {
    if (!menu) return undefined;

    const onPointerDown = (event: PointerEvent) => {
      if (!menuRef.current?.contains(event.target as Node)) closeMenu();
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') closeMenu();
    };
    document.addEventListener('pointerdown', onPointerDown, true);
    document.addEventListener('keydown', onKeyDown);
    window.addEventListener('resize', closeMenu);
    window.addEventListener('scroll', closeMenu, true);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown, true);
      document.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('resize', closeMenu);
      window.removeEventListener('scroll', closeMenu, true);
    };
  }, [closeMenu, menu]);

  const runAction = React.useCallback(async (action: SuperDocTableMenuAction) => {
    const point = pointRef.current;
    closeMenu();
    const ok = await executeSuperDocTableMenuAction(getSourceRef.current(), action, point);
    if (!ok) {
      toast.error(ACTION_FAILURE_MESSAGE);
      return;
    }
    onMutatedRef.current?.();
  }, [closeMenu]);

  const handleActionClick = React.useCallback((event: React.MouseEvent<HTMLButtonElement>) => {
    const key = event.currentTarget.dataset.action;
    if (!key || !(key in MENU_ACTIONS)) return;
    void runAction(MENU_ACTIONS[key as MenuActionKey]);
  }, [runAction]);

  if (!menu || typeof document === 'undefined') return null;

  return createPortal(
    <div
      ref={menuRef}
      className="min-w-44 overflow-visible rounded-md border border-stone-200 bg-white p-1 text-stone-950 shadow-md"
      style={{ position: 'fixed', top: pos.y, left: pos.x, zIndex: 10050 }}
      onContextMenu={preventNativeMenu}
    >
      <SubmenuItem label="Insert row" open={openSubmenu === 'row'} onOpen={openRowSubmenu}>
        <MenuItem action="insert-row-above" onClick={handleActionClick}>
          Above
        </MenuItem>
        <MenuItem action="insert-row-below" onClick={handleActionClick}>
          Below
        </MenuItem>
      </SubmenuItem>
      <SubmenuItem label="Insert column" open={openSubmenu === 'column'} onOpen={openColumnSubmenu}>
        <MenuItem action="insert-column-left" onClick={handleActionClick}>
          Left
        </MenuItem>
        <MenuItem action="insert-column-right" onClick={handleActionClick}>
          Right
        </MenuItem>
      </SubmenuItem>
      <div className="-mx-1 my-1 h-px bg-stone-100" />
      <MenuItem action="delete-row" onClick={handleActionClick} onMouseEnter={closeSubmenu}>
        Delete row
      </MenuItem>
      <MenuItem action="delete-column" onClick={handleActionClick} onMouseEnter={closeSubmenu}>
        Delete column
      </MenuItem>
      <MenuItem
        action="clear-cell-content"
        disabled={!canDeleteCell}
        onClick={handleActionClick}
        onMouseEnter={closeSubmenu}
      >
        Delete cell content
      </MenuItem>
      <div className="-mx-1 my-1 h-px bg-stone-100" />
      <MenuItem
        action="delete-table"
        destructive
        onClick={handleActionClick}
        onMouseEnter={closeSubmenu}
      >
        Delete table
      </MenuItem>
    </div>,
    document.body,
  );
}
